import { DashboardWithdrawalsComponent } from '../dashboard-withdrawals/dashboard-withdrawals.component';
import { CreditCollectionComponent } from '../credit-collection/credit-collection.component';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpService } from 'src/app/http.service';

interface DashboardColumn {
  key: string;
  title: string;
  highlight?: boolean;
}

interface DashboardSection {
  title: string;
  source: 'bank' | 'game' | 'seemless';
  itemTitle: string;
  columns: DashboardColumn[];
}

interface DashboardCompanyGroup {
  uuid: string;
  name: string;
  currency: string;
  rows: any[];
}

@Component({
  selector: 'app-consolidated-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, CreditCollectionComponent, DashboardWithdrawalsComponent],
  templateUrl: './consolidated-dashboard.component.html',
  styleUrls: ['../dashboard/dashboard.component.scss', './consolidated-dashboard.component.scss'],
})
export class ConsolidatedDashboardComponent implements OnInit {
  public is_loading = false;
  public error = '';
  public today = this.formatDate(new Date());
  public date = this.today;
  public companies: any[] = [];
  public bank: any[] = [];
  public game: any[] = [];
  public seemless: any[] = [];
  public companyGroups: Record<string, DashboardCompanyGroup[]> = {};
  private expandedFolders = new Set<string>();
  private requestId = 0;

  public sections: DashboardSection[] = [
    {
      title: 'Bank Summary', source: 'bank', itemTitle: 'Bank',
      columns: [
        { key: 'opening_balance', title: 'Opening' },
        { key: 'debit_adj_sum', title: 'Debit/In Adj' },
        { key: 'credit_adj_sum', title: 'Credit/Out Adj' },
        { key: 'tips_sum', title: 'Tips' },
        { key: 'profit_sum', title: 'Profit' },
        { key: 'debit_sum', title: 'Debit/In' },
        { key: 'credit_sum', title: 'Credit/Out' },
        { key: 'gw_fees', title: 'GW Fees' },
        { key: 'uc_sum', title: 'Balance', highlight: true },
      ],
    },
    {
      title: 'Seamless Game Summary', source: 'seemless', itemTitle: 'Game',
      columns: [
        { key: 'bet', title: 'Debit/In' },
        { key: 'winloss', title: 'Credit/Out' },
        { key: 'balance', title: 'Balance', highlight: true },
      ],
    },
    {
      title: 'Manual / Transfer Wallet Game Summary', source: 'game', itemTitle: 'Game',
      columns: [
        { key: 'opening_balance', title: 'Opening' },
        { key: 'debit_adj_sum', title: 'Debit/In Adj' },
        { key: 'credit_adj_sum', title: 'Credit/Out Adj' },
        { key: 'bonus_sum', title: 'Bonus' },
        { key: 'debit_sum', title: 'Debit/In' },
        { key: 'credit_sum', title: 'Credit/Out' },
        { key: 'balance', title: 'Balance', highlight: true },
        { key: 'dc_sum', title: 'In - Out - Bonus' },
      ],
    },
    {
      title: 'Unknown Payment Summary', source: 'bank', itemTitle: 'Bank',
      columns: [
        { key: 'upt_sum', title: 'Daily Unclaim' },
        { key: 'upc_sum', title: 'Daily Claimed' },
        { key: 'upb_sum', title: 'Total Unclaim Balance', highlight: true },
      ],
    },
  ];

  constructor(private http: HttpService) {}

  ngOnInit(): void {
    void this.init();
  }

  async init(refresh = false) {
    const currentRequest = ++this.requestId;
    this.is_loading = true;
    this.error = '';
    const response: any = await this.http.post('/admin/company/dashboard/consolidated', { date: this.date, refresh });
    if (currentRequest !== this.requestId) return;

    if (!response || !response.status) {
      this.companies = [];
      this.bank = [];
      this.game = [];
      this.seemless = [];
      this.companyGroups = {};
      this.error = response?.message || 'Unable to load consolidated dashboard.';
      this.is_loading = false;
      return;
    }

    this.companies = response.companies || [];
    this.bank = response.bank || [];
    this.game = response.game || [];
    this.seemless = response.seemless || [];
    this.groupCompanies();
    this.is_loading = false;
  }

  private groupCompanies(): void {
    for (const source of ['bank', 'game', 'seemless']) {
      const groups = new Map<string, DashboardCompanyGroup>();
      for (const company of this.companies) {
        groups.set(company.uuid, { ...company, rows: [] });
      }
      for (const row of this[source]) {
        const uuid = row.company_uuid;
        if (!groups.has(uuid)) {
          groups.set(uuid, { uuid, name: row.company_name, currency: row.currency, rows: [] });
        }
        groups.get(uuid)!.rows.push(row);
      }
      this.companyGroups[source] = [...groups.values()];
    }
  }

  isExpanded(section: DashboardSection, group: DashboardCompanyGroup): boolean {
    return this.expandedFolders.has(section.title + ':' + group.uuid);
  }

  toggleCompany(section: DashboardSection, group: DashboardCompanyGroup): void {
    const key = section.title + ':' + group.uuid;
    if (this.expandedFolders.has(key)) this.expandedFolders.delete(key);
    else this.expandedFolders.add(key);
  }

  setAllExpanded(expanded: boolean): void {
    this.expandedFolders.clear();
    if (expanded) {
      for (const section of this.sections) {
        for (const group of this.companyGroups[section.source] || []) {
          this.expandedFolders.add(section.title + ':' + group.uuid);
        }
      }
    }
  }

  rows(section: DashboardSection): any[] {
    return this[section.source] || [];
  }

  totalCurrency(rows: any[]): string {
    const currencies = [...new Set((rows || []).map(row => this.currencyLabel(row.currency)))];
    return currencies.length > 1 ? 'Mixed (unconverted)' : currencies[0] || '—';
  }

  total(rows: any[], key: string): number {
    // One combined subtotal of the displayed amounts; no exchange rate is applied.
    return (rows || [])
      .reduce((sum, row) => sum + (parseFloat(row[key]) || 0), 0);
  }

  currencyLabel(currency: string): string {
    return currency && currency !== 'none' ? currency : '';
  }

  private formatDate(date: Date): string {
    return date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
  }
}
